/**
 * Local-proxy OAuth instance routing.
 *
 * In local-proxy dev environments, OAuth providers redirect back to the shared
 * gateway at `local.ahoylocal.ai`, which reads an `oauth_slug` cookie to forward
 * the callback to the correct per-branch instance. The cookie value carries an
 * `api-` prefix so the gateway targets the API subdomain
 * (`api-{slug}.ahoylocal.ai`) where Django handles the callback — NOT the
 * frontend subdomain (`{slug}.ahoylocal.ai`) where Vite serves the SPA.
 *
 * Any SPA-initiated OAuth flow (one that navigates the browser to a provider
 * authorize URL) MUST call this before redirecting, or the callback will fail to
 * route. The legacy allauth/Google login flow does the equivalent inline in
 * `frontend/src/lib/allauth.js::redirectToProvider`; keep the two in sync.
 *
 * Outside `*.ahoylocal.ai` (i.e. production), this is a no-op, so it is safe to
 * call unconditionally before any server-side OAuth redirect.
 */
export function setOAuthInstanceRoutingCookie(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const { hostname } = window.location;
  // The shared gateway host itself is not an instance — never set a cookie there.
  if (hostname === 'local.ahoylocal.ai') {
    return;
  }
  // Legacy: {number}.local.ahoylocal.ai — New: {slug}.ahoylocal.ai
  const legacyMatch = hostname.match(/^(\d+)\.local\.ahoylocal\.ai$/);
  const slugMatch =
    hostname.match(/^([a-z0-9][a-z0-9-]*[a-z0-9])\.ahoylocal\.ai$/) ??
    hostname.match(/^([a-z0-9])\.ahoylocal\.ai$/);
  const match = legacyMatch ?? slugMatch;
  if (!match) {
    return;
  }
  const slug = match[1];
  document.cookie = `oauth_slug=api-${slug}; domain=.ahoylocal.ai; path=/; secure; samesite=lax; max-age=600`;
}
